import React, { useMemo } from 'react'
import type { EChartsOption } from 'echarts'
import EChartsCanvas from '../core/EChartsCanvas'
import { useAnalyticsTheme } from '../theme/useAnalyticsTheme'
import { buildAnalyticsAxisTooltip, buildHorizontalBarLayout } from '../theme/echartsTheme'

export type AnalyticsBarSeries = {
 name: string
 data: number[]
 color?: string
}

export type AnalyticsBarChartProps = {
 categories: string[]
 /** Single-series shorthand */
 data?: number[]
 /** Multi-series grouped bars */
 series?: AnalyticsBarSeries[]
 height?: number
 horizontal?: boolean
 color?: string
 zeroBarMuted?: boolean
 valueFormatter?: (value: number) => string
 seriesName?: string
 showLegend?: boolean
 loading?: boolean
 className?: string
}

const AnalyticsBarChart: React.FC<AnalyticsBarChartProps> = ({
 categories,
 data,
 series: seriesProp,
 height = 280,
 horizontal = false,
 color,
 zeroBarMuted = false,
 valueFormatter,
 seriesName = '',
 showLegend = false,
 loading = false,
 className,
}) => {
 const { mode, colors, baseOption, rtl } = useAnalyticsTheme()
 const barColor = color ?? colors.primary

 const resolvedSeries = useMemo<AnalyticsBarSeries[]>(() => {
  if (seriesProp?.length) return seriesProp
  return [{ name: seriesName, data: data ?? [], color: barColor }]
 }, [seriesProp, seriesName, data, barColor])

 const isSingleSeries = resolvedSeries.length === 1

 const empty =
  categories.length === 0 ||
  resolvedSeries.every((s) => s.data.every((v) => !Number.isFinite(v) || v === 0))

 const option = useMemo<EChartsOption>(() => {
  const tooltipBase =
   typeof baseOption.tooltip === 'object' && !Array.isArray(baseOption.tooltip)
    ? baseOption.tooltip
    : {}

  const axisTooltip: EChartsOption['tooltip'] = rtl
   ? buildAnalyticsAxisTooltip(tooltipBase as Record<string, unknown>, valueFormatter, { type: 'shadow' as const })
   : ({
      ...tooltipBase,
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      valueFormatter: valueFormatter
       ? (v) => valueFormatter(Number(Array.isArray(v) ? v[0] : v ?? 0))
       : undefined,
     } as EChartsOption['tooltip'])

  const baseGrid =
   typeof baseOption.grid === 'object' && !Array.isArray(baseOption.grid) ? baseOption.grid : {}
  const gridBottom = showLegend ? 48 : typeof baseGrid.bottom === 'number' ? baseGrid.bottom : 32

  const legend = showLegend
   ? {
      bottom: 0,
      left: 'center' as const,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: colors.label, fontSize: 11 },
     }
   : undefined

  const buildBarData = (values: number[], seriesColor: string, seriesIndex: number) =>
   values.map((v) => {
    const num = Number(v)
    let itemColor = seriesColor
    if (zeroBarMuted && isSingleSeries) {
     if (num < 0) itemColor = colors.negativeBar
     else if (num === 0) itemColor = colors.zeroBar
    }
    return {
     value: v,
     itemStyle: {
      color: itemColor,
      borderRadius: horizontal
       ? ((rtl ? [6, 0, 0, 6] : [0, 6, 6, 0]) as [number, number, number, number])
       : ([6, 6, 0, 0] as [number, number, number, number]),
     },
    }
   })

  const echartsSeries = resolvedSeries.map((s, i) => ({
   name: s.name,
   type: 'bar' as const,
   data: buildBarData(s.data, s.color ?? colors.palette[i % colors.palette.length], i),
   barMaxWidth: horizontal ? 18 : 28,
   ...(horizontal
    ? { barCategoryGap: isSingleSeries ? '38%' : '30%', barGap: '20%' }
    : { barCategoryGap: isSingleSeries ? '35%' : '30%', barGap: '20%' }),
   emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.12)' } },
  }))

  if (horizontal) {
   const layout = buildHorizontalBarLayout(categories, rtl, colors)

   return {
    backgroundColor: 'transparent',
    color: [...colors.palette],
    grid: {
     ...layout.grid,
     bottom: showLegend ? 48 : layout.grid.bottom,
     backgroundColor: 'transparent',
    },
    legend,
    tooltip: axisTooltip,
    xAxis: layout.xAxis,
    yAxis: layout.yAxis,
    series: echartsSeries,
   }
  }

  return {
   backgroundColor: 'transparent',
   color: [...colors.palette],
   grid: { ...baseGrid, bottom: gridBottom, containLabel: true },
   legend,
   tooltip: axisTooltip,
   xAxis: {
    type: 'category',
    data: categories,
    ...(rtl ? { inverse: true } : {}),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: colors.axisText, fontSize: 12, fontWeight: 500, interval: 'auto', hideOverlap: true },
   },
   yAxis: {
    type: 'value',
    ...(rtl ? { position: 'right' as const } : {}),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
     color: colors.label,
     fontSize: 11,
     formatter: valueFormatter ? (v: number) => valueFormatter(Number(v)) : undefined,
    },
    splitLine: { show: false },
   },
   series: echartsSeries,
  }
 }, [
  baseOption.grid,
  baseOption.tooltip,
  categories,
  colors,
  horizontal,
  isSingleSeries,
  resolvedSeries,
  rtl,
  showLegend,
  valueFormatter,
  zeroBarMuted,
 ])

 return (
  <EChartsCanvas
   option={option}
   height={height}
   className={className}
   loading={loading}
   empty={empty}
   themeKey={`bar-${mode}-${horizontal ? 'h' : 'v'}-${rtl ? 'rtl' : 'ltr'}-${resolvedSeries.length}`}
  />
 )
}

export default AnalyticsBarChart
